/**
 * The Stripe Checkout Session for buying (or renewing) self-funding access.
 *
 * The paywall creates it as soon as it shows the offer, so the price it displays is the amount that
 * session will charge; the checkout step then mounts the same session. It is cached for 25 minutes
 * (Stripe expires it at 30). Stripe brings the consumer back to the checkout step with
 * `checkout=complete`. A side trip's `returnTo` (where Back goes when the consumer doesn't buy) is
 * deliberately not carried through Stripe: after paying, the consumer goes on into self-funding. The
 * exceptions are renewing from Your Plan, which carries `then=your-plan` so the consumer comes back
 * to their plan, and choosing to self-fund from the options (`then=self-fund-choice`); nothing else is
 * accepted there. A 409 means access is already active.
 */
import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { SessionType } from '@shared';
import { clientStripeApi } from '@client/api/waterlily';
import { useClerkClientAuth } from '@client/hooks/useClerkClientAuth';
import { fetchSessionForAuthRequest, selectSession } from '@/model';
import { consumerStepPath } from '../lib/paths';

export const CHECKOUT_COMPLETE_PARAM = 'checkout';
export const AFTER_PURCHASE_PARAM = 'then';
const SESSION_REUSE_MS = 25 * 60 * 1000;

/**
 * Where a purchase leads other than into the self-funding sequence: back to Your Plan, or on to the
 * self-funding choice made from the options (see `useSelfFundChoicePath`).
 */
export type AfterPurchase = 'your-plan' | 'self-fund-choice' | null;

/**
 * Renewing from Your Plan (the paywall opened with `returnTo=your-plan`) comes back to Your Plan;
 * choosing to self-fund from the options goes on to that choice.
 */
export function afterPurchaseFor(returnTo: string | null): AfterPurchase {
  if (returnTo === 'your-plan') return 'your-plan';
  if (returnTo === 'options') return 'self-fund-choice';
  return null;
}

export function parseAfterPurchase(value: string | null): AfterPurchase {
  return value === 'your-plan' || value === 'self-fund-choice' ? value : null;
}

export function selfFundCheckoutReturnPath(
  clientId: string,
  afterPurchase: AfterPurchase = null,
): string {
  const path = `${consumerStepPath(clientId, 'self-fund-checkout')}?${CHECKOUT_COMPLETE_PARAM}=complete`;
  return afterPurchase ? `${path}&${AFTER_PURCHASE_PARAM}=${afterPurchase}` : path;
}

function isAlreadyActiveError(error: unknown): boolean {
  const status = (error as { status?: number; statusCode?: number | string } | null) ?? {};
  return (
    status.status === 409 ||
    String(status.statusCode) === '409' ||
    (error instanceof Error && /already have an active plan/i.test(error.message))
  );
}

export function useSelfFundCheckoutSession(
  clientId: string,
  { enabled, afterPurchase = null }: { enabled: boolean; afterPurchase?: AfterPurchase },
) {
  const dispatch = useDispatch();
  const session = useSelector(selectSession);
  const { clerkUserId } = useClerkClientAuth();
  const refetchedForMissingId = useRef(false);

  // A Clerk token refresh can leave the session without the user id until it is reloaded once.
  useEffect(() => {
    if (!enabled || clerkUserId || refetchedForMissingId.current) return;
    if (session.sessionType !== SessionType.client || session.isLoading || session.isInvalidating)
      return;
    refetchedForMissingId.current = true;
    dispatch(fetchSessionForAuthRequest({ invalidating: true }));
  }, [
    enabled,
    clerkUserId,
    dispatch,
    session.isInvalidating,
    session.isLoading,
    session.sessionType,
  ]);

  // Creating a session is a POST, but caching it as a query lets the paywall and the checkout step
  // share one session (and one amount) instead of creating a new one on each screen.
  const query = useQuery({
    queryKey: ['self-fund-checkout-session', clientId, afterPurchase],
    queryFn: () =>
      clientStripeApi.createCheckoutSession({
        clientAuthUserId: clerkUserId ?? '',
        returnPath: selfFundCheckoutReturnPath(clientId, afterPurchase),
        uiMode: 'embedded',
      }),
    enabled: enabled && !!clerkUserId,
    staleTime: SESSION_REUSE_MS,
    gcTime: SESSION_REUSE_MS,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const alreadyActive = query.isError && isAlreadyActiveError(query.error);

  return {
    clientSecret: query.data?.clientSecret ?? null,
    amountInCents: query.data?.amountInCents ?? null,
    isPending: enabled && (query.isPending || !clerkUserId),
    isError: query.isError && !alreadyActive,
    alreadyActive,
    retry: () => {
      void query.refetch();
    },
  };
}
